import { useCallback, useRef, useState } from "react";
import type { Transition } from "history";
import { useBlocker, useLocation } from "react-router-dom";
import { Button, Modal } from "@monolith-forensics/monolith-ui";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import { getPlatform } from "@/hooks/usePlatform";
import type { UploadQueueItem } from "./useUploadQueue";

const UNFINISHED_PHASES: UploadQueueItem["phase"][] = [
  "queued",
  "uploading",
  "finalizing",
];

export const hasUnfinishedUploads = (
  uploads: Record<string, UploadQueueItem>
) => Object.values(uploads).some((u) => UNFINISHED_PHASES.includes(u.phase));

export type UseLeaveWhileUploadingGuardOptions = {
  /** The `uploads` from `useUploadQueue`. */
  uploads: Record<string, UploadQueueItem>;
  /**
   * Paths that keep the upload queue mounted (the same item's other tabs),
   * so navigating to them needs no confirm.
   */
  isSameView: (pathname: string) => boolean;
};

/**
 * Interim guard until uploads survive leaving the page (ENG-2123): while the
 * queue has unfinished uploads, in app navigation away from the view asks
 * first, because leaving unmounts the queue and cancels them. Render the
 * returned `dialog`.
 *
 * Built on the router's `useBlocker`, which blocks through `history.block`.
 * That also registers a `beforeunload` prompt, so the browser asks before a
 * refresh, close or typed URL. Electron's main process doesn't handle
 * `will-prevent-unload`, so a renderer `beforeunload` would stop the window
 * from closing or reloading with no prompt; the guard is off in Electron.
 */
export const useLeaveWhileUploadingGuard = ({
  uploads,
  isSameView,
}: UseLeaveWhileUploadingGuardOptions) => {
  const [pending, setPending] = useState<Transition | null>(null);
  // `useBlocker` unblocks for good once a transition is let through; a new
  // blocker identity makes it block again. Keyed on the location, so it only
  // re-blocks after the let through navigation has landed: a Back/Forward
  // retry is an async go(), and blocking again before it arrives would make
  // history revert it again, in a loop.
  const { key: locationKey } = useLocation();
  const isSameViewRef = useRef(isSameView);
  isSameViewRef.current = isSameView;

  const blocker = useCallback(
    (tx: Transition) => {
      if (isSameViewRef.current(tx.location.pathname)) {
        tx.retry();
        return;
      }
      setPending(tx);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locationKey]
  );

  useBlocker(
    blocker,
    hasUnfinishedUploads(uploads) && !getPlatform().isElectron
  );

  const stay = () => setPending(null);
  const leave = () => {
    const tx = pending;
    setPending(null);
    tx?.retry();
  };

  const dialog = pending ? (
    <Modal open={true} onClose={stay} showCloseButton={true}>
      <Modal.Title>Upload in progress</Modal.Title>
      <div style={{ margin: "20px 0px", width: "350px" }}>
        If you leave this page, uploads that haven't finished will be cancelled.
      </div>
      <ButtonMenu>
        <Button size="xs" variant="subtle" onClick={leave}>
          Leave anyway
        </Button>
        <Button size="xs" variant="contained" color="primary" onClick={stay}>
          Stay
        </Button>
      </ButtonMenu>
    </Modal>
  ) : null;

  return { dialog };
};
